/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'

import { Field, Form, Formik } from 'formik'
import Recaptcha from 'react-recaptcha'
import context from '../core/context'
import { requestQuote } from '../core/controller'

function Captcha({ verifyCallback }) {
  const { sitekey = '', recaptcha = '' } = window.sapiens

  if (recaptcha && sitekey) {
    if (recaptcha === 'v2' && window.grecaptcha) {
      return <Recaptcha sitekey={sitekey} render="explicit" verifyCallback={verifyCallback} />
    }

    if (recaptcha === 'v3') {
      return (
        <p className="form__recaptcha">
          This site is protected by reCAPTCHA and the Google
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and
          <a href="https://policies.google.com/terms">Terms of Service</a> apply.
        </p>
      )
    }
  }

  return null
}

Captcha.propTypes = {
  verifyCallback: PropTypes.func,
}

Captcha.defaultProps = {
  verifyCallback: () => {},
}

function QuoteStep({ labels, dark }) {
  const { filters, selectedDevelopers, setStep, reset } = useContext(context)
  const [errorMessage, setErrorMessage] = useState('')

  const validateEmail = value => {
    let error

    if (!value) {
      error = 'This field is Required'
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
      error = 'Invalid email address'
    }

    return error
  }

  const validateRequired = value => {
    let error

    if (!value) {
      error = 'This field is Required'
    }

    return error
  }

  const validateNumber = value => {
    let error

    if (value && Number.isNaN(Number(value))) {
      error = 'Please enter a number'
    }

    return error
  }

  const handleSubmit = async (values, FormikBag) => {
    const { sitekey = '', recaptcha = '' } = window.sapiens
    const data = {
      skills: filters.skill,
      developers: selectedDevelopers,
      ...values,
    }

    if (sitekey && window.grecaptcha) {
      if (recaptcha === 'v2') {
        if (!data['g-recaptcha-response']) {
          FormikBag.setErrors({
            recaptcha: 'This field is Required',
          })

          return
        }
      }

      if (recaptcha === 'v3') {
        await window.grecaptcha.execute(sitekey, { action: 'submit' }).then(token => {
          data['g-recaptcha-response'] = token
        })
      }
    }

    const quote = await requestQuote(data)
    const { error, message } = quote

    if (error) {
      setErrorMessage(message)
    } else {
      reset()
      setStep(3)
      document.dispatchEvent(new CustomEvent('step-updated'))
    }
  }

  const prevStep = () => {
    setStep(1)
    document.dispatchEvent(new CustomEvent('step-updated'))
  }

  return (
    <div className="developer-search__view">
      <div className="developer-search__header">
        {labels.headline && (
          <h2
            className={`headline headline--hero ${dark ? 'text-white' : 'text-deep-blue'}`}
            dangerouslySetInnerHTML={{ __html: labels.headline }}
          />
        )}
        <div className="developer-search__view__header">
          {labels.step_3.headline && (
            <h3 className={`headline headline--md ${dark ? 'text-white' : 'text-deep-blue'}`}>
              {labels.step_3.headline}
            </h3>
          )}
        </div>
      </div>
      <div className="developer-search__wrapper">
        <Formik
          initialValues={{
            name: '',
            last_name: '',
            email: '',
            phone_number: '',
            company: '',
            employees: '',
            terms_and_conditions: false,
          }}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, setFieldValue }) => (
            <Form className="form">
              <div className="form__cols">
                <div className="form__group">
                  <label className="form__label" htmlFor="name">
                    Name
                  </label>
                  <Field type="text" name="name" />
                </div>
                <div className="form__group">
                  <label className="form__label form__label--required" htmlFor="last_name">
                    Last Name
                  </label>
                  <Field type="text" name="last_name" validate={validateRequired} />
                  {errors.last_name && touched.last_name && (
                    <div className="form__helper form__helper--error">{errors.last_name}</div>
                  )}
                </div>
              </div>
              <div className="form__cols">
                <div className="form__group">
                  <label className="form__label form__label--required" htmlFor="email">
                    Email
                  </label>
                  <Field type="email" name="email" validate={validateEmail} />
                  {errors.email && touched.email && (
                    <div className="form__helper form__helper--error">{errors.email}</div>
                  )}
                </div>
                <div className="form__group">
                  <label className="form__label" htmlFor="phone_number">
                    Phone Number
                  </label>
                  <Field type="text" name="phone_number" />
                </div>
              </div>
              <div className="form__cols">
                <div className="form__group">
                  <label className="form__label form__label--required" htmlFor="company">
                    Company
                  </label>
                  <Field type="text" name="company" validate={validateRequired} />
                  {errors.company && touched.company && (
                    <div className="form__helper form__helper--error">{errors.company}</div>
                  )}
                </div>
                <div className="form__group">
                  <label className="form__label" htmlFor="employees">
                    No. of Employees
                  </label>
                  <Field type="number" name="employees" validate={validateNumber} />
                  {errors.employees && touched.employees && (
                    <div className="form__helper form__helper--error">{errors.employees}</div>
                  )}
                </div>
              </div>
              <div className="form__group sr-only">
                <Field type="checkbox" name="terms_and_conditions" />
              </div>
              <Captcha
                verifyCallback={token => {
                  setFieldValue('g-recaptcha-response', token)
                }}
              />
              {errors.recaptcha && (
                <div className="form__helper form__helper--error">{errors.recaptcha}</div>
              )}
              {errorMessage && (
                <div className="form__helper form__helper--error">{errorMessage}</div>
              )}
              <div className="developer-search__nav form__submit">
                <button
                  type="button"
                  className={`btn ${dark ? 'btn--green text-white' : 'btn--white text-deep-blue'}`}
                  onClick={prevStep}
                  disabled={isSubmitting}
                >
                  {labels.step_3.prev_step_cta_label}
                </button>
                <button
                  className={`btn ${dark ? 'btn--white text-deep-blue' : 'btn--deep-blue text-white'}${
                    isSubmitting ? ' btn--loading' : ''
                  }`}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {labels.step_3.next_step_cta_label}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

QuoteStep.propTypes = {
  labels: PropTypes.object.isRequired,
  dark: PropTypes.bool.isRequired,
}

export default QuoteStep
